import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { getContentModerationError, isNotEnoughCreditsError } from '@/api/errors';
import {
  cancelGeneratorRun,
  deleteGeneratorResults,
  getModelRunStatuses,
  listGeneratorRuns,
  runGeneratorModel,
  toModelInput,
} from '@/api/generate.api';
import { log } from '@/logger/logger';
import { useWorkflowStore } from '@/state/workflow.state';
import type { GenerateRequest, GeneratorRunListResponse } from '@/api/generate.api';
import type { ModelRunCredits } from '@/api/modelPrediction';
import type { MediaAsset } from '@/types/api/assets';
import type { ToolOutputEntry } from '@/components/Tool/ToolOutputGroups';
import type { ToolOutputRemovalTarget } from '@/components/Tool/ToolOutputActions';

const logger = log.getLogger('useGeneratorRuns');

const STATUS_POLL_INTERVAL_MS = 1500;
const STATUS_POLL_MAX_INTERVAL_MS = 15_000;

/** One output. A Generate with a count of N makes N of these, and each one ends on its own. */
export type GeneratorRun = {
  /** Made on the page for runs started here, the prediction id for runs from history. */
  key: string;
  /** Set once the server has started the run. */
  predictionId?: string;
  /** `canceling` is hidden and no longer locks Generate, but is still checked, as Cancel can come too late. */
  status: 'starting' | 'running' | 'canceling' | 'completed' | 'failed';
  prompt: string;
  startedAt: string;
  results?: MediaAsset[];
  /** The reason the server gave, when it gave one. */
  error?: string;
  /** The server's own prompt check refused the run before it started. */
  promptBlocked?: boolean;
  /** The balance couldn't pay for the run, so the server refused it before it started. */
  creditsRefused?: boolean;
  /** Only runs started on this visit have one. They share a section in the gallery. */
  generateId?: string;
  /** Width over height the run asked for, so its card has the result's shape before it arrives. */
  aspectRatio?: number;
};

/** A Generate whose runs have all answered, and the server refused some of them before they started. */
export type GeneratorFailure = {
  failed: GeneratorRun[];
  total: number;
};

type UseGeneratorRunsOptions = {
  /** Signed-out visitors can't run anything and have no history to load. */
  signedIn: boolean;
  onGenerateFailed: (failure: GeneratorFailure) => void;
  onHistoryFailed: () => void;
  onCancelFailed: () => void;
  /** The balance after a run was charged or refunded. */
  onBalance: (credits: ModelRunCredits) => void;
};

const isPending = (run: GeneratorRun) => run.status === 'starting' || run.status === 'running';

/** An `aspect_ratio` setting such as `16:9`. Other values, such as `auto`, leave the shape to the model. */
function toAspectRatio(value: unknown): number | undefined {
  const match = typeof value === 'string' ? /^(\d+):(\d+)$/.exec(value) : null;
  return match && Number(match[2]) > 0 ? Number(match[1]) / Number(match[2]) : undefined;
}

/**
 * The server's reason when it sent one, such as missing credits. A request that never got an answer only
 * has transport text like "Network Error", so it has no reason.
 */
function serverReason(error: unknown): string | undefined {
  if (!isAxiosError<{ message?: unknown }>(error)) return undefined;
  const message = error.response?.data?.message;
  return typeof message === 'string' && message ? message : undefined;
}

/** The server lists only finished and running runs, so errors from before a reload are not shown again. */
function toHistoryRuns(page: GeneratorRunListResponse): GeneratorRun[] {
  return page.runs.flatMap((item): GeneratorRun[] => {
    if (item.status !== 'COMPLETED' && item.status !== 'RUNNING') return [];
    return [
      {
        key: item.predictionId,
        predictionId: item.predictionId,
        status: item.status === 'COMPLETED' ? 'completed' : 'running',
        prompt: item.prompt ?? '',
        startedAt: item.createdAt,
        results: item.results,
      },
    ];
  });
}

/**
 * What a run shows in the gallery, in its place: a loading card, its results, or a failed card. One Generate
 * is one section. Runs from history can't be matched to their Generate, so each one gets its own section.
 * A run the server refused before it started has no card, because nothing ran: the page's banner says why.
 */
function toOutputs(run: GeneratorRun): ToolOutputEntry[] {
  const batchId = run.generateId ?? run.key;
  const placeholder = { key: run.key, batchId, runStartedAt: run.startedAt, aspectRatio: run.aspectRatio };
  switch (run.status) {
    case 'starting':
    case 'running':
      return [{ ...placeholder, placeholder: 'loading' }];
    case 'failed':
      return run.predictionId ? [{ ...placeholder, placeholder: 'failed', message: run.error }] : [];
    case 'canceling':
      return [];
    case 'completed':
      return (run.results ?? []).map((asset, index) => ({
        ...asset,
        name: run.prompt,
        input: { prompt: run.prompt },
        runId: run.predictionId,
        batchId,
        runStartedAt: run.startedAt,
        inputIndex: index,
      }));
  }
}

/**
 * The generate page's outputs: the runs started on this visit and the user's earlier runs, in one list.
 * Each output is started and checked on its own, so a slow or failed one never holds back the others.
 * One loop checks every run that is still going, whether it was started here or before a reload.
 */
export function useGeneratorRuns({
  signedIn,
  onGenerateFailed,
  onHistoryFailed,
  onCancelFailed,
  onBalance,
}: UseGeneratorRunsOptions) {
  // TODO: move every change to `runs` into one reducer, so how a run's status can change is written in one place.
  const [runs, setRuns] = useState<GeneratorRun[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Signed-out visitors have no history, so for them it counts as loaded from the start.
  const [historyLoaded, setHistoryLoaded] = useState(!signedIn);
  const runsRef = useRef(runs);
  const totalsByGenerate = useRef(new Map<string, number>());
  // Runs cancelled before their start request answered. Their cancel goes out once the run has an id.
  const cancelOnStart = useRef(new Set<string>());
  const callbacks = useRef({ onGenerateFailed, onHistoryFailed, onCancelFailed, onBalance });
  callbacks.current = { onGenerateFailed, onHistoryFailed, onCancelFailed, onBalance };

  useEffect(() => {
    runsRef.current = runs;
  }, [runs]);

  const updateRun = useCallback((key: string, patch: Partial<GeneratorRun>) => {
    setRuns((current) => current.map((run) => (run.key === key ? { ...run, ...patch } : run)));
  }, []);

  /**
   * History can list a run before its start request answers. Only the copy made here is kept, because it is
   * the one its Generate waits for before it reports failures.
   */
  const markStarted = useCallback((key: string, predictionId: string) => {
    setRuns((current) =>
      current.flatMap((run): GeneratorRun[] => {
        if (run.key === key) {
          return [{ ...run, status: run.status === 'canceling' ? 'canceling' : 'running', predictionId }];
        }
        return run.predictionId === predictionId ? [] : [run];
      }),
    );
  }, []);

  const removeRun = useCallback((key: string) => {
    setRuns((current) => current.filter((run) => run.key !== key));
  }, []);

  /** A cancel the server didn't take puts the run back, so the user can try again. */
  const requestCancel = useCallback((key: string, predictionId: string) => {
    cancelGeneratorRun(predictionId).catch((error: unknown) => {
      logger.error('cancelGeneratorRun(): error', error);
      setRuns((current) =>
        current.map((run) => (run.key === key && run.status === 'canceling' ? { ...run, status: 'running' } : run)),
      );
      callbacks.current.onCancelFailed();
    });
  }, []);

  /** Adds runs from a history page after the ones already shown, skipping any the page already has. */
  const addHistoryPage = useCallback((page: GeneratorRunListResponse) => {
    // The tiles read their signed URLs from the store, so the URLs go in before the runs that need them.
    useWorkflowStore.getState().mergeResolvedAssets(page.resolvedAssets ?? {});
    const loaded = toHistoryRuns(page);
    setRuns((current) => {
      const known = new Set(current.map((run) => run.predictionId));
      return [...current, ...loaded.filter((run) => !known.has(run.predictionId))];
    });
    setNextCursor(page.nextCursor);
  }, []);

  const loadHistory = useCallback(
    async (cursor: string | undefined, signal?: AbortSignal) => {
      const { data } = await listGeneratorRuns(cursor, signal);
      addHistoryPage(data);
    },
    [addHistoryPage],
  );

  // The newest page of history. Run is not locked while it loads: a run from before the reload that is
  // still going only locks Run once it arrives here.
  useEffect(() => {
    if (!signedIn) return;
    const controller = new AbortController();
    loadHistory(undefined, controller.signal).then(
      () => setHistoryLoaded(true),
      (error: unknown) => {
        if (controller.signal.aborted) return;
        logger.error('listGeneratorRuns(): error', error);
        callbacks.current.onHistoryFailed();
        setHistoryLoaded(true);
      },
    );
    return () => controller.abort();
  }, [signedIn, loadHistory]);

  // A failed status check changes no run. It only waits longer before the next one, so a network problem
  // never shows a run as failed. Runs that get stuck on the server are expired there, so every run ends.
  useEffect(() => {
    const controller = new AbortController();
    let interval = STATUS_POLL_INTERVAL_MS;
    let timer: ReturnType<typeof setTimeout>;
    const check = async () => {
      const isChecked = (run: GeneratorRun) => run.status === 'running' || run.status === 'canceling';
      const ids = runsRef.current.flatMap((run) => (isChecked(run) && run.predictionId ? [run.predictionId] : []));
      if (ids.length > 0) {
        try {
          const { data } = await getModelRunStatuses(ids, controller.signal);
          useWorkflowStore.getState().mergeResolvedAssets(data.resolvedAssets ?? {});
          callbacks.current.onBalance(data);
          setRuns((current) =>
            current.flatMap((run): GeneratorRun[] => {
              const status = isChecked(run) && run.predictionId ? data.predictions[run.predictionId] : undefined;
              if (!status) return [run];
              switch (status.status) {
                // a run that finished before its cancel arrived keeps its result
                case 'COMPLETED':
                  return [{ ...run, status: 'completed', results: status.results }];
                case 'FAILED':
                  return run.status === 'canceling' ? [] : [{ ...run, status: 'failed', error: status.error }];
                case 'CANCELED':
                  return [];
                default:
                  return [run];
              }
            }),
          );
          interval = STATUS_POLL_INTERVAL_MS;
        } catch (error) {
          if (controller.signal.aborted) return;
          logger.warn('getModelRunStatuses(): error, checking again later', error);
          interval = Math.min(interval * 2, STATUS_POLL_MAX_INTERVAL_MS);
        }
      }
      if (!controller.signal.aborted) {
        timer = setTimeout(() => void check(), interval);
      }
    };
    timer = setTimeout(() => void check(), interval);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, []);

  // Reports a Generate once all its start requests have answered, so N refusals make one message and not N.
  // Runs that fail later show on their own card instead.
  useEffect(() => {
    totalsByGenerate.current.forEach((total, generateId) => {
      const own = runs.filter((run) => run.generateId === generateId);
      if (own.some((run) => run.status === 'starting')) return;
      totalsByGenerate.current.delete(generateId);
      const failed = own.filter((run) => run.status === 'failed' && !run.predictionId);
      if (failed.length > 0) {
        callbacks.current.onGenerateFailed({ failed, total });
      }
    });
  }, [runs]);

  const generate = useCallback(
    (request: GenerateRequest) => {
      const count = Math.max(1, Math.floor(request.count));
      const generateId = crypto.randomUUID();
      const startedAt = new Date().toISOString();
      // Every output sends the same assets, which were uploaded before Generate could be pressed.
      const input = toModelInput({ ...request.params, ...request.inputs }, request.prompt);
      const aspectRatio = toAspectRatio(request.params.aspect_ratio);
      const created = Array.from({ length: count }, (): GeneratorRun => ({
        key: crypto.randomUUID(),
        status: 'starting',
        prompt: request.prompt,
        startedAt,
        generateId,
        aspectRatio,
      }));
      totalsByGenerate.current.set(generateId, count);
      setRuns((current) => [...created, ...current]);
      created.forEach((run) => {
        runGeneratorModel(request.modelId, input).then(
          ({ data }) => {
            const canceled = cancelOnStart.current.delete(run.key);
            if (!('predictionId' in data)) {
              return canceled ? removeRun(run.key) : updateRun(run.key, { status: 'failed' });
            }
            markStarted(run.key, data.predictionId);
            callbacks.current.onBalance(data);
            if (canceled) requestCancel(run.key, data.predictionId);
          },
          // A request with no answer counts as failed too. If the server did start that run, it shows up
          // in history after the next reload.
          (error: unknown) => {
            logger.error('runGeneratorModel(): error', error);
            if (cancelOnStart.current.delete(run.key)) return removeRun(run.key);
            updateRun(run.key, {
              status: 'failed',
              error: serverReason(error),
              promptBlocked: getContentModerationError(error) !== null,
              creditsRefused: isNotEnoughCreditsError(error),
            });
          },
        );
      });
    },
    [markStarted, removeRun, requestCancel, updateRun],
  );

  /** Stops every run still being made. Each run is refunded by the server; one that already ended keeps its result. */
  const cancel = useCallback(() => {
    const pending = runsRef.current.filter(isPending);
    if (pending.length === 0) return;
    const keys = new Set(pending.map((run) => run.key));
    setRuns((current) => current.map((run) => (keys.has(run.key) ? { ...run, status: 'canceling' } : run)));
    pending.forEach((run) => {
      if (run.predictionId) {
        requestCancel(run.key, run.predictionId);
      } else {
        cancelOnStart.current.add(run.key);
      }
    });
  }, [requestCancel]);

  /** Removes finished results on the server, then from the page. Rejects, and changes nothing, when the server fails. */
  const remove = useCallback(async (targets: readonly ToolOutputRemovalTarget[]) => {
    const predictionIds = [...new Set(targets.flatMap(({ runId }) => (runId ? [runId] : [])))];
    if (predictionIds.length === 0) return;
    await deleteGeneratorResults(
      predictionIds,
      targets.map(({ id }) => id),
    );
    const removed = new Set(targets.map(({ id, runId }) => `${runId}:${id}`));
    setRuns((current) =>
      current.map((run) =>
        run.status === 'completed' && run.results
          ? { ...run, results: run.results.filter((asset) => !removed.has(`${run.predictionId}:${asset.id}`)) }
          : run,
      ),
    );
  }, []);

  const loadMore = useCallback(() => {
    if (!nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    loadHistory(nextCursor)
      .then(
        () => undefined,
        (error: unknown) => {
          logger.error('listGeneratorRuns(): error', error);
          callbacks.current.onHistoryFailed();
        },
      )
      .finally(() => setIsLoadingMore(false));
  }, [isLoadingMore, loadHistory, nextCursor]);

  const outputs = useMemo(() => runs.flatMap(toOutputs), [runs]);
  const pendingCount = useMemo(() => runs.filter(isPending).length, [runs]);

  return {
    /** For the gallery: results and placeholders, each Generate's runs in the order they were asked for. */
    outputs,
    /** Outputs still being made. Each gets a placeholder, and while any is left, Run is locked. */
    pendingCount,
    /** True once the first page of history has answered, or failed to. The page waits for it to pick its layout. */
    historyLoaded,
    generate,
    cancel,
    remove,
    canLoadMore: nextCursor !== null,
    isLoadingMore,
    loadMore,
  };
}
